"use client";

import { RefObject, useCallback, useEffect, useRef } from "react";

const DRAWER_SELECTOR = "[data-wse-nav-drawer]";
const BACKDROP_SELECTOR = "[data-wse-nav-backdrop]";
const PANEL_SELECTOR = "[data-wse-nav-panel]";
const TRANSITION_MS = 300;
const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function getDrawer(containerRef: RefObject<HTMLElement | null>) {
  return containerRef.current?.querySelector<HTMLElement>(DRAWER_SELECTOR) ?? null;
}

function setDrawerState(drawer: HTMLElement, open: boolean) {
  drawer.dataset.open = String(open);
  if (open) drawer.hidden = false;
}

function getDrawerContent(drawer: HTMLElement): HTMLElement | null {
  return drawer.querySelector<HTMLElement>(PANEL_SELECTOR);
}

function setDrawerLayers(drawer: HTMLElement) {
  drawer.style.zIndex = "50";
  drawer.classList.add("wse-mobile-drawer");
  const backdrop = drawer.querySelector<HTMLElement>(BACKDROP_SELECTOR);
  backdrop?.style.setProperty("z-index", "0");
  backdrop?.classList.add("wse-mobile-drawer-backdrop");
  getDrawerContent(drawer)?.classList.add("wse-mobile-drawer-content");
}

export function useMobileDrawerController(
  containerRef: RefObject<HTMLElement | null>,
  deps: readonly unknown[]
) {
  const isOpenRef = useRef(false);
  const previousOverflowRef = useRef<string | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openFrameRef = useRef<number | null>(null);

  const clearScheduledTransitions = useCallback(() => {
    if (closeTimerRef.current !== null) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    if (openFrameRef.current !== null) {
      cancelAnimationFrame(openFrameRef.current);
      openFrameRef.current = null;
    }
  }, []);

  const close = useCallback(() => {
    const drawer = getDrawer(containerRef);
    clearScheduledTransitions();
    isOpenRef.current = false;
    if (drawer) {
      setDrawerState(drawer, false);
      closeTimerRef.current = setTimeout(() => {
        drawer.hidden = true;
        closeTimerRef.current = null;
      }, TRANSITION_MS);
    }
    if (previousOverflowRef.current !== null) {
      document.body.style.overflow = previousOverflowRef.current;
      previousOverflowRef.current = null;
    }
  }, [clearScheduledTransitions, containerRef]);

  const open = useCallback(() => {
    const drawer = getDrawer(containerRef);
    if (!drawer) return;

    clearScheduledTransitions();
    isOpenRef.current = true;
    previousOverflowRef.current ??= document.body.style.overflow;
    drawer.hidden = false;
    setDrawerState(drawer, false);
    setDrawerLayers(drawer);
    openFrameRef.current = requestAnimationFrame(() => {
      drawer.dataset.open = "true";
      openFrameRef.current = null;
    });
    drawer.querySelector<HTMLElement>(FOCUSABLE_SELECTOR)?.focus();
    document.body.style.overflow = "hidden";
  }, [clearScheduledTransitions, containerRef]);

  useEffect(() => {
    const drawer = getDrawer(containerRef);
    if (!drawer) return;

    if (isOpenRef.current) {
      setDrawerState(drawer, true);
      setDrawerLayers(drawer);
      document.body.style.overflow = "hidden";
    } else {
      setDrawerState(drawer, false);
      drawer.hidden = true;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        close();
        return;
      }
      if (event.key !== "Tab") return;

      const focusable = Array.from(
        drawer.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
      );
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable.at(-1);
      if (!last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    const handleClick = (event: MouseEvent) => {
      const target = event.target as Element | null;
      if (
        target?.closest('[data-wse-nav-action="mobile-close"]') ||
        target?.closest("a[href]") ||
        target?.closest(BACKDROP_SELECTOR) ||
        target === drawer
      ) {
        close();
      }
    };

    drawer.addEventListener("keydown", handleKeyDown);
    drawer.addEventListener("click", handleClick);
    return () => {
      drawer.removeEventListener("keydown", handleKeyDown);
      drawer.removeEventListener("click", handleClick);
      clearScheduledTransitions();
    };
    // Fragment DOM is replaced when these dependencies change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clearScheduledTransitions, containerRef, close, ...deps]);

  useEffect(() => {
    return () => {
      isOpenRef.current = false;
      clearScheduledTransitions();
      const drawer = getDrawer(containerRef);
      if (drawer) {
        setDrawerState(drawer, false);
        drawer.hidden = true;
      }
      if (previousOverflowRef.current !== null) {
        document.body.style.overflow = previousOverflowRef.current;
        previousOverflowRef.current = null;
      }
    };
  }, [clearScheduledTransitions, containerRef]);

  return { open, close };
}
